import { useState, useEffect } from 'react';
import { logger } from '../utils/logger';

const UPDATE_INTERVAL_MS = 80; // ~12 updates per seconde is genoeg voor de visualizer
const LEVEL_STEP = 1 / 20; // resolutie van de visualizer-bars

type LevelListener = (level: number) => void;

interface LevelEntry {
    source: MediaStreamAudioSourceNode;
    analyser: AnalyserNode;
    data: Uint8Array;
    listeners: Set<LevelListener>;
    rafId: number;
    lastUpdate: number;
    lastLevel: number;
}

// Eén gedeelde AudioContext en één meet-loop per stream, hoeveel visualizers
// er ook tegelijk renderen (opnamebalk, inline kaart, wizard-preview).
let sharedAudioContext: AudioContext | null = null;
const levelEntries = new Map<MediaStream, LevelEntry>();

const getSharedAudioContext = (): AudioContext | null => {
    if (sharedAudioContext && sharedAudioContext.state !== 'closed') {
        return sharedAudioContext;
    }

    try {
        sharedAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
        logger.error('Error creating shared audio context:', error);
        sharedAudioContext = null;
    }

    return sharedAudioContext;
};

const teardownEntry = (stream: MediaStream, entry: LevelEntry): void => {
    cancelAnimationFrame(entry.rafId);
    try {
        entry.source.disconnect();
    } catch (error) {
        // Bron was al losgekoppeld (bijv. doordat de stream is gestopt).
    }
    levelEntries.delete(stream);

    if (levelEntries.size === 0 && sharedAudioContext && sharedAudioContext.state === 'running') {
        void sharedAudioContext.suspend().catch(() => undefined);
    }
};

const subscribeToAudioLevel = (stream: MediaStream, listener: LevelListener): (() => void) => {
    const ctx = getSharedAudioContext();
    if (!ctx) {
        return () => undefined;
    }

    if (ctx.state === 'suspended') {
        void ctx.resume().catch(() => undefined);
    }

    let entry = levelEntries.get(stream);
    if (!entry) {
        let source: MediaStreamAudioSourceNode;
        let analyser: AnalyserNode;
        try {
            source = ctx.createMediaStreamSource(stream);
            analyser = ctx.createAnalyser();
        } catch (error) {
            logger.error('Error initializing audio level visualizer:', error);
            return () => undefined;
        }

        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.5;
        source.connect(analyser);

        const newEntry: LevelEntry = {
            source,
            analyser,
            data: new Uint8Array(analyser.frequencyBinCount),
            listeners: new Set(),
            rafId: 0,
            lastUpdate: 0,
            lastLevel: 0
        };

        const update = (timestamp: number) => {
            newEntry.rafId = requestAnimationFrame(update);

            if (timestamp - newEntry.lastUpdate < UPDATE_INTERVAL_MS) {
                return;
            }
            newEntry.lastUpdate = timestamp;

            newEntry.analyser.getByteFrequencyData(newEntry.data);

            // Calculate RMS (Root Mean Square) for better volume estimation
            let sum = 0;
            for (let i = 0; i < newEntry.data.length; i++) {
                sum += newEntry.data[i] * newEntry.data[i];
            }
            const rms = Math.sqrt(sum / newEntry.data.length);

            // Normalize to 0-1 range (approximate max value for 8-bit data is 255, but RMS is usually lower)
            // We use a slightly lower max to make it more sensitive
            const normalized = Math.min(1, rms / 128);

            // Kwantiseer naar bar-resolutie zodat alleen zichtbare wijzigingen een render triggeren.
            const level = Math.round(normalized / LEVEL_STEP) * LEVEL_STEP;
            if (level !== newEntry.lastLevel) {
                newEntry.lastLevel = level;
                newEntry.listeners.forEach(notify => notify(level));
            }
        };

        newEntry.rafId = requestAnimationFrame(update);
        levelEntries.set(stream, newEntry);
        entry = newEntry;
    }

    const activeEntry = entry;
    activeEntry.listeners.add(listener);
    listener(activeEntry.lastLevel);

    return () => {
        activeEntry.listeners.delete(listener);
        if (activeEntry.listeners.size === 0) {
            teardownEntry(stream, activeEntry);
        }
    };
};

export const useAudioLevel = (stream: MediaStream | null, isEnabled: boolean = true) => {
    const [level, setLevel] = useState(0);

    useEffect(() => {
        if (!stream || !isEnabled || stream.getAudioTracks().length === 0) {
            setLevel(0);
            return;
        }

        return subscribeToAudioLevel(stream, setLevel);
    }, [stream, isEnabled]);

    return level;
};

export default useAudioLevel;
